import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  bootstrapCurrentUser,
  bootstrapCurrentPermission,
  bootstrapSettingsDetails,
} from '@/store/bootstrap/actions';
import { fetchLogin, login, logout } from '@/store/auth/actions';
import { fetchAllUserPreferences } from '@/store/ServerStorage';

import { selectIsLoggingIn, selectIsLoggedIn } from '@/store/auth/selectors';
import {
  selectBootstrapLoading,
  selectBootstrapError,
  selectCurrUser,
  selectSettings,
} from '@/store/bootstrap/selectors';
import { clearLocalStorage } from '@/utils/storageKeys';

export const useAuth = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const isBootstraping = useSelector(selectBootstrapLoading);
  const bootstrapError = useSelector(selectBootstrapError);
  const currUser = useSelector(selectCurrUser);
  const settingsDetails = useSelector(selectSettings);

  const isLoggingIn = useSelector(selectIsLoggingIn);
  const isLoggedIn = useSelector(selectIsLoggedIn);

  const authKey = 'apiv1Auth';

  const getAuth = () => {
    return localStorage.getItem(authKey);
  };

  const setAuth = (accessToken) => {
    localStorage.setItem(authKey, accessToken);
  };

  const deleteAuth = () => {
    localStorage.removeItem(authKey);
  };

  const doFetchLogin = async () => {
    try {
      return await dispatch(fetchLogin()).unwrap();
    } catch (error) {
      console.error('Error fetching login', error);
    }
  };

  const doLogin = async ({ email, password, otp = '' }) => {
    try {
      const result = await dispatch(login({ email, password, otp })).unwrap();
      const accessToken = result?.data?.access_token;
      if (accessToken) setAuth(accessToken);
      return result;
    } catch (error) {
      console.error('Error logging in', error);
    }
  };
  const doLogout = async () => {
    try {
      await dispatch(logout());
      clearLocalStorage();
      history.push('/login');
    } catch (error) {
      console.error('Error logging out', error);
    }
  };

  const bootstrap = async () => {
    try {
      const currUserRes = await dispatch(bootstrapCurrentUser()).unwrap();
      dispatch(bootstrapSettingsDetails());
      if (currUserRes?.data?.account?.status === 'suspended') {
        return currUserRes;
      }
      if (!currUserRes?.data?.id) {
        return currUserRes;
      }
      const [currPermissionRes] = await Promise.all([
        dispatch(bootstrapCurrentPermission()).unwrap(),
        fetchAllUserPreferences(),
      ]);
      return [currUserRes, currPermissionRes];
    } catch (error) {
      console.error('Error bootstrapping', error);
    }
  };

  return {
    getAuth,
    setAuth,
    deleteAuth,
    fetchLogin: doFetchLogin,
    login: doLogin,
    logout: doLogout,
    bootstrap,

    isBootstraping,
    bootstrapError,
    currUser,
    settingsDetails,

    isLoggingIn,
    isLoggedIn,
  };
};
